import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import axios from 'axios';
import { useState, type ReactElement } from 'react';
import { Controller, useForm, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { BrandPanel } from './_components/BrandPanel';
import { PasswordField } from './_components/PasswordField';
import { StepHeading } from './_components/StepHeading';
import { policyI18nKey, validatePassword, type PasswordIssue } from './_lib/passwordPolicy';
import { tokens } from '../../design/tokens';
import { Routes } from '../../routes/routes';
import { useAuthStore } from '../../stores';

/**
 * `ResetConfirmPage` (AUT-110) — submit new password using a reset token.
 *
 * URL: `/auth/reset/confirm?token=<raw>`. On 200: redirect to
 * `/auth/login` with a success indicator passed via location.state so
 * the LoginStep can render a toast/banner. On 400 token-invalid:
 * "ссылка устарела" + link to `/auth/reset`. On 422 policy: inline.
 *
 * Confirm DOES NOT auto-login (architect R2 — TOTP cannot be bypassed).
 */

const FormSchema = z
  .object({
    password: z.string().min(1, 'auth.invite.setPassword.policy.tooShort'),
    confirm: z.string().min(1, 'auth.invite.setPassword.policy.mismatch'),
  })
  .refine((d) => d.password === d.confirm, {
    path: ['confirm'],
    message: 'auth.invite.setPassword.policy.mismatch',
  });

type FormValues = z.infer<typeof FormSchema>;

type PageError =
  | { kind: 'policy'; issue: PasswordIssue }
  | { kind: 'token_invalid' }
  | { kind: 'server'; key: string };

const mapConfirmError = (err: unknown): PageError => {
  if (!axios.isAxiosError(err) || !err.response) {
    return { kind: 'server', key: 'auth.errors.network' };
  }
  const status = err.response.status;
  const data = err.response.data as
    | { code?: string; details?: { reason?: PasswordIssue } }
    | undefined;
  if (status === 422 && data?.code === 'admin_password_policy_violation') {
    const reason = data.details?.reason;
    if (reason) return { kind: 'policy', issue: reason };
  }
  if (status === 400 || data?.code === 'admin_password_reset_token_invalid') {
    return { kind: 'token_invalid' };
  }
  if (status >= 500) return { kind: 'server', key: 'auth.errors.server' };
  return { kind: 'server', key: 'auth.errors.unknown' };
};

type FieldsProps = {
  control: Control<FormValues>;
  disabled: boolean;
  passwordLabel: string;
  confirmLabel: string;
};

const ResetFields = ({
  control,
  disabled,
  passwordLabel,
  confirmLabel,
}: FieldsProps): ReactElement => (
  <>
    <Controller
      name="password"
      control={control}
      render={({ field, fieldState }) => (
        <PasswordField
          {...field}
          fullWidth
          autoComplete="new-password"
          label={passwordLabel}
          disabled={disabled}
          error={Boolean(fieldState.error)}
          inputProps={{ 'aria-label': passwordLabel, 'data-testid': 'reset-confirm-input' }}
          sx={{ mb: '16px' }}
        />
      )}
    />
    <Controller
      name="confirm"
      control={control}
      render={({ field, fieldState }) => (
        <PasswordField
          {...field}
          fullWidth
          autoComplete="new-password"
          label={confirmLabel}
          disabled={disabled}
          error={Boolean(fieldState.error)}
          inputProps={{ 'aria-label': confirmLabel, 'data-testid': 'reset-confirm-confirm-input' }}
          sx={{ mb: '16px' }}
        />
      )}
    />
  </>
);

type ConfirmFormProps = { token: string };

const ConfirmForm = ({ token }: ConfirmFormProps): ReactElement => {
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState<PageError | null>(null);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { password: '', confirm: '' },
    mode: 'onSubmit',
  });

  const onSubmit = async (values: FormValues): Promise<void> => {
    setPageError(null);
    const policy = validatePassword(values.password);
    if (!policy.ok) {
      setPageError({ kind: 'policy', issue: policy.reason });
      return;
    }
    setSubmitting(true);
    try {
      await authStore.confirmPasswordReset(token, values.password);
      navigate(Routes.Auth.Login, {
        replace: true,
        state: { resetSuccess: true },
      });
    } catch (err) {
      setPageError(mapConfirmError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (pageError?.kind === 'token_invalid') {
    return <TokenInvalidNotice />;
  }
  return (
    <ConfirmFormBody
      submitting={submitting}
      pageError={pageError}
      hasMismatch={Boolean(errors.confirm)}
      onSubmit={handleSubmit(onSubmit)}
      control={control}
    />
  );
};

type ConfirmFormBodyProps = {
  submitting: boolean;
  pageError: PageError | null;
  hasMismatch: boolean;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  control: Control<FormValues>;
};

const ConfirmFormBody = ({
  submitting,
  pageError,
  hasMismatch,
  onSubmit,
  control,
}: ConfirmFormBodyProps): ReactElement => {
  const { t } = useTranslation();
  const inlineError: string | null = pageError
    ? pageError.kind === 'policy'
      ? t(policyI18nKey(pageError.issue))
      : pageError.kind === 'server'
        ? t(pageError.key)
        : null
    : hasMismatch
      ? t('auth.invite.setPassword.policy.mismatch')
      : null;

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      <StepHeading
        eyebrow={t('auth.login.step.eyebrow')}
        title={t('auth.reset.confirm.title')}
        subtitle={t('auth.reset.confirm.subtitle')}
      />
      <ResetFields
        control={control}
        disabled={submitting}
        passwordLabel={t('auth.reset.confirm.passwordLabel')}
        confirmLabel={t('auth.reset.confirm.confirmLabel')}
      />
      {inlineError && (
        <Alert severity="error" sx={{ mb: '16px' }} data-testid="reset-confirm-error">
          {inlineError}
        </Alert>
      )}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={submitting}
        sx={{ height: 52, borderRadius: '14px' }}
        data-testid="reset-confirm-submit"
      >
        {t('auth.reset.confirm.cta')}
      </Button>
    </Box>
  );
};

const TokenInvalidNotice = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box>
      <StepHeading
        eyebrow={t('auth.login.step.eyebrow')}
        title={t('auth.reset.confirm.title')}
        subtitle={t('auth.reset.confirm.tokenInvalid')}
      />
      <Alert severity="error" sx={{ mb: '16px' }} data-testid="reset-confirm-token-invalid">
        {t('auth.reset.confirm.tokenInvalid')}
      </Alert>
      <Box sx={{ textAlign: 'center' }}>
        <Link
          component={RouterLink}
          to={Routes.Auth.ResetRequest}
          underline="hover"
          sx={{ fontSize: 13 }}
        >
          {t('auth.reset.confirm.requestNewLink')}
        </Link>
      </Box>
    </Box>
  );
};

const MissingTokenNotice = TokenInvalidNotice;

export const ResetConfirmPage = (): ReactElement => {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: tokens.surface.base }}>
      <BrandPanel step="login" email="" />
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '56px',
        }}
      >
        <Paper elevation={0} sx={{ width: '100%', maxWidth: 420, background: 'transparent' }}>
          {token ? <ConfirmForm token={token} /> : <MissingTokenNotice />}
        </Paper>
      </Box>
    </Box>
  );
};
